import { z } from 'zod';

export interface Owner {
  id: number;
  last_name: string;
  first_name: string;
}

export interface Plan {
  id: number;
  title: string;
  slug: string;
}

export interface Technician {
  id: number;
  connected_to: number;
  technician: {
    id: number;
    slug: string;
    last_name: string;
    first_name: string;
    email: string;
    avatar: string;
  };
  is_lead: boolean;
}

export interface isServiceProviderExistsResponse {
  is_pest_company: boolean;
  is_active: boolean;
  is_admin: boolean;
  my_company_slug: string;
}

export interface EmailMessagesSend {
  mct_receive_messages: boolean;
  srbm_receive_messages: boolean;
  smst_receive_messages: boolean;
  srst_receive_messages: boolean;
}

export interface ObjectInfo {
  id: number;
  slug: string;
  name: string;
  address: string;
  owner: Owner | null;
  manager: Owner | null;
  trap_count: number;
  email_messagess_send: EmailMessagesSend;
  technicians: Technician[];
}

export interface UserOnObject {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  avatar?: string;
}
export interface UserOnObjectResponse {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  role: string;
}
export interface SiteTechnician {
  is_lead: boolean;
  technician: UserOnObject;
}

export interface ApiObjectResponse {
  id: number;
  slug: string;
  full_name: string;
  name: string;
  city: string;
  street: string;
  building_number: string;
  apartment_no: string | null;
  owner: number | null;
  company: number | null;
  owners_to_add: UserOnObject[];
  technicians: SiteTechnician[];
  manager: number | null;
  country: string | null;
  zip_code: string | null;
}
export interface CompanyPestResponse {
  id: number;
  name: string;
  slug: string;
  city: string;
  street: string;
  building_no: string;
  apartment_no: string;
  owner: Owner;
  manager: Owner | null;
  object_count: number;
  trap_count: number;
  EDRPOU: string;
  image: string;
}
export interface Client {
  id: number;
  name: string;
  slug: string;
  city: string;
  street: string;
  building_no: string;
  apartment_no: string;
  owner: Owner;
  manager: Owner | null;
  object_count: number;
  trap_count: number;
  objects_list: ObjectInfo[];
  EDRPOU: string;
  image: string;
  country: string;
  zip_code: string;
  business_structure: string;
}

export interface ApiClientsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Client[];
}

export interface getTechnicianToObjectResponse {
  id: number;
  email: string;
  slug: string;
  label: string;
}

export const objectSchema = z.object({
  full_name: z.string().optional().default(''),
  name: z.string().min(1, { message: 'Required field' }),
  city: z.string().min(1, { message: 'Required field' }),
  street: z.string().min(1, { message: 'Required field' }),
  company: z.number().min(1, { message: 'Required field' }),
  building_number: z.string().min(1, { message: 'Required field' }),
  apartment_no: z.string().optional().default(''),
  owner: z.number({ required_error: 'Required field' }).nullable(),
  technicians: z.array(z.number()).min(1, { message: 'At least one technician is required' }),
  manager: z.number().min(1, { message: 'Required field' }),
  country: z.string().min(1, { message: 'Required field' }).nullable(),
  zip_code: z.string().min(1, { message: 'Required field' }).nullable(),
});

export type ObjectFormValues = z.infer<typeof objectSchema>;

export const siteFormSchema = objectSchema.omit({
  owner: true,
});

export type SiteFormValues = z.infer<typeof siteFormSchema>;

export const newPositionSchema = z.object({
  object_slug: z.string(),
  latitude: z.number(),
  longitude: z.number(),
});

export const ownerSchema = z.object({
  ownerId: z.string().min(1, { message: 'Owner is required' }),
});

export type OwnerFormsValues = z.infer<typeof ownerSchema>;

export type newPositionValues = z.infer<typeof newPositionSchema>;

export const technicianSiteSchema = z.object({
  technician: z.string().min(1, { message: 'Technician is required' }),
  is_lead: z.boolean(),
});

export type TechnicianSiteFormValues = z.infer<typeof technicianSiteSchema>;

export const clientManageSchema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  EDRPOU: z.string().optional(),
  business_structure: z.string().optional(),
  country: z.string().min(1, { message: 'Country is required' }),
  city: z.string().min(1, { message: 'City is required' }),
  zip_code: z.string().min(1, { message: 'Zip code is required' }),
  street: z.string().min(1, { message: 'Street is required' }),
  building_no: z.string().min(1, { message: 'Building № is required' }),
  apartment_no: z.string().optional(),
  image: z.union([z.string(), z.instanceof(File), z.null()]),
  owner: z
    .union([
      z.string().min(1, { message: 'Owner is required' }),
      z.object({
        first_name: z.string().min(1, { message: 'First name is required' }),
        last_name: z.string().min(1, { message: 'Last name is required' }),
        email: z.string().email({ message: 'Invalid email' }),
        password: z.string().min(6, { message: 'Password too short' }),
        confirm_password: z.string().min(6, { message: 'Confirm password too short' }),
      }),
    ])
    .optional(),
  technician: z.string().min(1, { message: 'Technician is required' }),
  manager: z.string().nullable().optional(),
});
export const clientUpdateModalSchema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  EDRPOU: z.string().optional(),
  city: z.string().min(1, { message: 'City is required' }),
  street: z.string().min(1, { message: 'Street is required' }),
  building_no: z.string().min(1, { message: 'Building № is required' }),
  apartment_no: z.string().optional(),
  image: z.union([z.string(), z.instanceof(File), z.null()]),
  business_structure: z.string().optional(),
  zip_code: z.string().optional(),
  country: z.string().optional(),
  owner: z
    .union([
      z.string().min(1, { message: 'Owner is required' }),
      z.object({
        first_name: z.string().min(1, { message: 'First name is required' }),
        last_name: z.string().min(1, { message: 'Last name is required' }),
        email: z.string().email({ message: 'Invalid email' }),
        password: z.string().min(6, { message: 'Password too short' }),
        confirm_password: z.string().min(6, { message: 'Confirm password too short' }),
      }),
    ])
    .optional(),
  technician: z.string().optional(),
  manager: z.string().nullable().optional(),
});
export type ClientUpdateModalFormValues = z.infer<typeof clientUpdateModalSchema>;

export const clientPostCompany = z.object({
  name: z.string().min(1, { message: 'Name is Required' }),
  description: z.string().optional(),
  ownership_form: z.string().optional(),
  logo: z.instanceof(File).optional(),
  EDRPOU: z.string().optional(),
  zip_code: z.string().optional(),
  country: z.string().optional(),
  city_text: z.string().optional(),
  street: z.string().optional(),
  building_no: z.string().optional(),
  apartment_no: z.string().optional(),
});
export type ClientFormValues = z.infer<typeof clientManageSchema>;

export const clientUpdateSchema = z.object({
  name: z.string().min(1, { message: 'Required field' }),
  EDRPOU: z.string().min(1, { message: 'Required field' }),
  city: z.string().min(1, { message: 'Required field' }),
  street: z.string().min(1, { message: 'Required field' }),
  building_no: z.string().min(1, { message: 'Required field' }),
  apartment_no: z.string().min(1, { message: 'Required field' }),

  owner: z.string().min(1, { message: 'Required field' }),
  image: z.union([z.string(), z.instanceof(File), z.null()]).optional(),
});

export type ClientUpdateFormValues = z.infer<typeof clientUpdateSchema>;

export const clientWithObjectSchema = z.object({
  name: z.string().min(1, { message: 'Name is Required' }),
  EDRPOU: z.string().min(1, { message: 'Business ID is required' }),
  business_structure: z.string().min(1, { message: 'Business Structure is required' }),
  country: z.string().min(1, { message: 'Country is required' }),
  city: z.string().min(1, { message: 'City is required' }),
  zip_code: z.string().min(1, { message: 'Postal/Zip Code is required' }),
  street: z.string().min(1, { message: 'Street is required' }),
  building_no: z.string().min(1, { message: 'Building № is required' }),
  apartment_no: z.string().min(1, { message: 'Required field' }),
  full_name: z.string().optional(),
  owner: z.number().nullable().optional(),
  technician: z.number().nullable().optional(),
});

export const clientWithPestCompany = z.object({
  name: z.string().min(1, { message: 'Name is Required' }),
  ownership_form: z.string().min(1, { message: 'Ownership Form is required' }),
  description: z.string().optional(),
  logo: z.instanceof(File).optional(),
  EDRPOU: z.string().optional(),
  zip_code: z.string().min(1, { message: 'Postal/Zip Code is required' }),
  country: z.string().min(1, { message: 'Country is required' }),
  city_text: z.string().min(1, { message: 'City is required' }),
  street: z.string().min(1, { message: 'Street is required' }),
  building_no: z.string().min(1, { message: 'Building № is required' }),
  apartment_no: z.string().min(1, { message: 'Required field' }),
});
export const clientWithPestCompanyEdit = z.object({
  name: z.string().optional(),
  ownership_form: z.string().optional(),
  description: z.string().optional(),
  logo: z.instanceof(File).optional(),
  EDRPOU: z.string().optional(),
  zip_code: z.string().optional(),
  country: z.string().optional(),
  city_text: z.string().optional(),
  street: z.string().optional(),
  building_no: z.string().optional(),
  apartment_no: z.string().optional(),
  id: z.number().optional(),
});
export type ClientWithPestCompanyEditValues = z.infer<typeof clientWithPestCompanyEdit>;
export type ClientFormValuesPestCompany = z.infer<typeof clientWithPestCompany>;
export type ClientFormValuesWithObject = z.infer<typeof clientWithObjectSchema>;

export const EmailNotifySchema = z.object({
  mct_receive_messages: z.boolean(),
  srbm_receive_messages: z.boolean(),
  smst_receive_messages: z.boolean(),
  srst_receive_messages: z.boolean(),
});

export type EmailNotifyValues = z.infer<typeof EmailNotifySchema>;

export interface TechnicianInfo {
  id: number;
  slug: string;
  last_name: string;
  first_name: string;
  email: string;
  avatar: string;
}

export interface TechnicianAssignment {
  id: number;
  connected_to: number;
  technician: TechnicianInfo;
  is_lead: boolean;
}

export interface EmailMessagesSend {
  mct_receive_messages: boolean;
  sbs_receive_messages: boolean;
}

export interface TrapActivity {
  sequence_number: number;
  slug: string;
  tripped_caught_count: number;
  tripped_empty_count: number;
}

export interface SRBMActivity {
  sequence_number: number;
  slug: string;
  eaten_bait: number;
}

export interface ObjectItem {
  id: number;
  slug: string;
  name: string;
  address: string;
  full_name: string;
  city: string;
  street: string;
  building_number: string;
  apartment_no: string;
  company: number;
  owner: null | {
    id: number;
    last_name: string;
    first_name: string;
  };
  manager: null | {
    id: number;
    last_name: string;
    first_name: string;
  };
  mct_count: number;
  srbm_count: number;
  smst_count: number;
  srst_count: number;
  email_messagess_send: EmailMessagesSend;
  technicians: TechnicianAssignment[];
  position: [number, number] | null;
  is_activity: boolean;
  activity_status: string;
  mct_activity: TrapActivity[];
  srbm_activity: SRBMActivity[];
  smst_activity: TrapActivity[];
  srst_activity: TrapActivity[];
}

export interface GroupedObject {
  id: number;
  name: string;
  slug: string;
  city: string;
  street: string;
  building_no: string;
  apartment_no: string;
  owner: Owner;
  manager: Owner | null;
  object_count: number;
  mct_count: number;
  srbm_count: number;
  srst_count: number;
  smst_count: number;
  object_list: ObjectItem[];
  EDRPOU: string;
  image: string;
}

export const addImageToClientSchema = z.object({
  image: z.instanceof(File),
});

export type AddImageToClientValues = z.infer<typeof addImageToClientSchema>;

export interface MyClientProvider {
  apartment_no: string;
  building_no: string;
  choosen_tarriff: string;
  city_text: string;
  country: string | null;
  description: string;
  id: number;
  EDRPOU: string;
  is_active: boolean;
  latitude: number | null;
  legal_name: string;
  logo: string;
  longtitude: number | null;
  name: string;
  owner: number;
  ownership_form: string;
  payed_till: string;
  payed_traps: number;
  slug: string;
  street: string;
  zip_code: string | null;
}
